import {
	OrderProduct,
	MatchedProduct,
	Shopper,
	OrderObject,
	OrderInfo,
	ResponsiblePerson,
	OrderDelivery,
	Duration,
} from 'common/types';
import { getNextProductReturnTime } from 'common/utils/dateCalculations';
import {
	getShopperNameKeywords,
	getShopperNames,
	getShopperIdsWithProductsFromShoppers,
	getCategoryIdsFromShoppers,
	getStockProductIdsFromProducts,
	getUniqProductTypes,
} from 'common/utils/rentalUtils';
import { getOrderPricing, getPricingOptions } from 'common/modules/orders';
import { api } from 'common/frontend/api';
import { InventoryItem } from 'common/modules/inventory';
import { getProductCodes } from 'services/utils';

export const fetchScannedStockItemFromDb = async (
	barcodeString: string,
	shopId: string,
): Promise<InventoryItem | undefined> => {
	const inventoryItems = await api()
		.inventoryItems.get.where('shopId', '==', shopId)
		.whereArray('identifiers', 'array-contains', barcodeString)
		.get();
	// each identifier is unique, so there should always be only one StockItem to return.
	return inventoryItems[0] ?? undefined;
};

export const getMatchedOrderProductsFromActiveShopper = (
	scannedProductIds: string[],
	activeShopperId: string | null,
	rentalProducts: OrderProduct[],
) => {
	if (activeShopperId) {
		const activeShopperProducts = rentalProducts.filter((p) => p.shopperId === activeShopperId);
		const matchedProductsFromActiveShopper = activeShopperProducts.reduce(
			(matchedProductIds: MatchedProduct[], p) => {
				if (p.set) {
					p.setProducts.forEach((setP, index) => {
						if (scannedProductIds.includes(setP.productApiId) && !setP.removedFromParent) {
							const matchedProduct: MatchedProduct = {
								productApiId: p.productApiId,
								orderProductId: p.id,
								setIndex: index,
								productCode: setP.productCode,
								selectedVariant: setP.variant,
							};
							matchedProductIds.push(matchedProduct);
						}
					});
				} else {
					if (scannedProductIds.includes(p.productApiId)) {
						const matchedProduct: MatchedProduct = {
							productApiId: p.productApiId,
							orderProductId: p.id,
							setIndex: null,
							productCode: p.productCode,
							selectedVariant: p.variant,
						};
						matchedProductIds.push(matchedProduct);
					}
				}
				return matchedProductIds;
			},
			[],
		);

		return matchedProductsFromActiveShopper;
	} else {
		// TODO - activeshopperId should not be empty.
		return [];
	}
};

const getUpdatedShoppersWithNewProducts = (
	shoppers: Shopper[],
	productsToAdd: OrderProduct[],
): Shopper[] => {
	const updatedShoppers: Shopper[] = shoppers.map((shopper) => {
		const newProductsForShopper = productsToAdd.filter((p) => p.shopperId === shopper.id);
		const newProductIdsForShopper = newProductsForShopper.map((p) => p.id);
		const allProductIdsForShopper = [...shopper.productIds, ...newProductIdsForShopper];
		const newCategoryIdsForShopper = [
			...new Set(
				newProductsForShopper.flatMap((item) =>
					item.categoryIds && item.categoryIds.length ? item.categoryIds : [],
				),
			),
		];
		const allCategoryIdsForShopper = [...(shopper.categoryIds || []), ...newCategoryIdsForShopper];

		const updatedShopper: Shopper = {
			...shopper,
			productIds: allProductIdsForShopper,
			categoryIds: allCategoryIdsForShopper,
		};
		return updatedShopper;
	});
	return updatedShoppers;
};

export const createUpdatedRentalOrderObject = (
	rentalInfo: OrderInfo,
	rentalProducts: OrderProduct[],
	productsToAdd: OrderProduct[],
	shoppers: Shopper[],
	orderDelivery?: OrderDelivery,
): OrderObject => {
	const updatedProducts = [...rentalProducts, ...productsToAdd];
	const orderPricing = getOrderPricing(
		updatedProducts,
		orderDelivery,
		getPricingOptions(rentalInfo.pricing),
	);
	const updatedShoppers = getUpdatedShoppersWithNewProducts(shoppers, productsToAdd);
	const updatedShopperIdsWithProducts = getShopperIdsWithProductsFromShoppers(updatedShoppers);
	const updatedCategoryIds = getCategoryIdsFromShoppers(updatedShoppers);
	const updatedStockProductIds = getStockProductIdsFromProducts(updatedProducts);
	const updatedReturnTimeNext = getNextProductReturnTime(updatedProducts);
	const updatedShopperNameKeywords = getShopperNameKeywords(
		updatedShoppers,
		rentalInfo.responsiblePerson,
	);
	const updatedShopperNames = getShopperNames(updatedShoppers, rentalInfo.responsiblePerson);
	const updatedRentalInfo: OrderInfo = {
		...rentalInfo,
		categoryIds: updatedCategoryIds,
		stockProductIds: updatedStockProductIds,
		shopperIdsWithProducts: updatedShopperIdsWithProducts,
		pricing: orderPricing,
		shopperNameKeywords: updatedShopperNameKeywords,
		shopperNames: updatedShopperNames,
		returnTimeNext: updatedReturnTimeNext,
		includedProductTypes: getUniqProductTypes(updatedProducts),
	};

	return {
		rentalInfo: updatedRentalInfo,
		products: updatedProducts,
		shoppers: updatedShoppers,
		orderDelivery,
	};
};

export const getUpdatedRentalInfoFromShopperToDelete = (
	rental: OrderObject,
	shopperToDelete: Shopper,
): OrderInfo => {
	const { rentalInfo, products, shoppers, orderDelivery } = rental;
	const updatedShoppers = shoppers.filter((shopper) => shopper.id !== shopperToDelete.id);
	const updatedProducts = products.filter((product) => product.shopperId !== shopperToDelete.id);
	const updatedCategoryIds = getCategoryIdsFromShoppers(updatedShoppers);
	const orderPricing = getOrderPricing(
		updatedProducts,
		orderDelivery,
		getPricingOptions(rentalInfo.pricing),
	);
	const updatedReturnTimeNext = getNextProductReturnTime(updatedProducts);
	const updatedShopperIdsWithProducts = getShopperIdsWithProductsFromShoppers(updatedShoppers);
	const updatedStockProductIds = getStockProductIdsFromProducts(updatedProducts);
	// Make shopperToDelete to be external responsible person if shopperToDelete was responsible person
	const { responsiblePerson } = rentalInfo;
	let updatedResponsiblePerson: ResponsiblePerson = { ...responsiblePerson };
	if (!responsiblePerson.external && responsiblePerson.shopperId === shopperToDelete.id) {
		const { id, firstName, lastName, email, phone, marketing } = shopperToDelete;
		updatedResponsiblePerson = {
			external: true,
			person: { id, firstName, lastName, email, phone, marketing },
		};
	}
	const updatedShopperNameKeywords = getShopperNameKeywords(
		updatedShoppers,
		updatedResponsiblePerson,
	);
	const updatedShopperNames = getShopperNames(updatedShoppers, updatedResponsiblePerson);
	const updatedRentalInfo: OrderInfo = {
		...rentalInfo,
		categoryIds: updatedCategoryIds,
		pricing: orderPricing,
		responsiblePerson: updatedResponsiblePerson,
		returnTimeNext: updatedReturnTimeNext,
		shopperIdsWithProducts: updatedShopperIdsWithProducts,
		shopperNameKeywords: updatedShopperNameKeywords,
		shopperNames: updatedShopperNames,
		stockProductIds: updatedStockProductIds,
		rentalProductCodes: getProductCodes(updatedProducts),
		includedProductTypes: getUniqProductTypes(updatedProducts),
	};
	return updatedRentalInfo;
};

export const getDurationTypeFromProducts = (products: OrderProduct[]) => {
	if (products.length) {
		return products[0].durationType;
	}
	return '24h';
};

export const getDurationNameFromProducts = (products: OrderProduct[]) => {
	if (products.length) {
		return products[0].durationName || null;
	}
	return null;
};

export const getDurationFromProducts = (products: OrderProduct[]): Duration | null => {
	if (products.length) {
		const firstProduct = products[0];
		return {
			durationInSeconds: firstProduct.rentalDurationInSeconds,
			durationType: firstProduct.durationType,
			durationName: firstProduct.durationName,
		};
	}
	return null;
};
